import {DebugElement, InputSignal} from '@angular/core';
import {ComponentFixture} from '@angular/core/testing';
import {By} from '@angular/platform-browser';

export namespace TestUtils {
  /**
   * This methods sets an input signal of the {@link fixture.componentInstance} component in a type-safe way.
   * @param fixture The fixture of the component.
   * @param key The key of the input signal.
   * @param value The new value passed to the signal.
   * @copyright https://stackoverflow.com/a/79445223/27163452
   */
  export function updateInputSignal<TComponent, TKey extends keyof TComponent, TValue extends TComponent[TKey] extends InputSignal<infer U> ? U : never>(
    fixture: ComponentFixture<TComponent>,
    key: TKey & string,
    value: TValue
  ): void {
    fixture.componentRef.setInput(key, value);
  }

  /**
   * Queries the DOM for an element with the attribute data-testid=dataTestId
   * @param fixture The fixture of the component.
   * @param dataTestId The data-testid attribute. (eg. data-testid="apples-component")
   */
  export function queryByTestId<TComponent>(fixture: ComponentFixture<TComponent>, dataTestId: string): DebugElement {
    return fixture.debugElement.query(By.css(`[data-testid="${dataTestId}"]`));
  }

  /**
   * Queries the DOM for an elements with the attribute data-testid=dataTestId
   * @param fixture The fixture of the component.
   * @param dataTestId The data-testid attribute. (eg. data-testid="apples-component")
   */
  export function queryAllByTestId<TComponent>(fixture: ComponentFixture<TComponent>, dataTestId: string): DebugElement[] {
    return fixture.debugElement.queryAll(By.css(`[data-testid="${dataTestId}"]`));
  }

  /**
   * This methods gets the value of an element property using the queryByTestId.
   * @param fixture The fixture of the component.
   * @param dataTestId The data-testid attribute. (eg. data-testid="apples-component")
   * @param propertyName The name of the needed property.
   */
  export function queryPropertyValue<TProperty = void>(fixture: ComponentFixture<any>, dataTestId: string, propertyName: string): TProperty | undefined {
    return queryByTestId(fixture, dataTestId)?.properties[propertyName];
  }
}
